/**
 * Чистые помощники обучения (волна W3): уровни геймификации, таймкоды глав,
 * точки-навигатор теста, полоска дней траектории, «Ждут подтверждения» у
 * наставника. Без React — покрыты vitest (learn-utils.test.ts).
 */
import type { QuizAnswerInput, QuizClientQuestion } from '../../lib/wikiApi';

// ─── Уровни (Стажёр → Повар → Су-шеф → Шеф) ─────────────────────────

/**
 * Пороги уровней — константа фронта (план §1.4: уровни C, без бэка). Очки
 * считает крон wiki-gamification: урок 5, тест 20 (+10 с первой попытки),
 * курс 15, неделя подряд 30 — «Шеф» ≈ полгода регулярного обучения.
 */
export const LEVELS = [
  { name: 'Стажёр', min: 0 },
  { name: 'Повар', min: 300 },
  { name: 'Су-шеф', min: 800 },
  { name: 'Шеф', min: 1500 },
] as const;

export interface LevelInfo {
  name: string;
  index: number;
  /** Следующий уровень; null — уже «Шеф». */
  next: string | null;
  /** Сколько очков до следующего уровня (0 на максимуме). */
  toNext: number;
  /** Прогресс внутри текущего уровня, 0–100 (на максимуме — 100). */
  pct: number;
}

export function levelOf(points: number): LevelInfo {
  const p = Math.max(0, Math.floor(Number.isFinite(points) ? points : 0));
  let index = 0;
  for (let i = 0; i < LEVELS.length; i++) if (p >= LEVELS[i].min) index = i;
  const cur = LEVELS[index];
  const nxt = LEVELS[index + 1] ?? null;
  if (!nxt) return { name: cur.name, index, next: null, toNext: 0, pct: 100 };
  const pct = Math.round(((p - cur.min) / (nxt.min - cur.min)) * 100);
  return { name: cur.name, index, next: nxt.name, toNext: nxt.min - p, pct: Math.max(0, Math.min(100, pct)) };
}

// ─── Таймкоды ────────────────────────────────────────────────────────

/** 65 → «1:05», 3723 → «1:02:03». */
export function fmtTimecode(sec: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Текущая глава видео по позиции (последняя с t ≤ позиции). */
export function currentChapterIndex(chapters: Array<{ t: number }>, positionSec: number): number {
  let idx = -1;
  for (let i = 0; i < chapters.length; i++) if (chapters[i].t <= positionSec) idx = i;
  return idx;
}

// ─── Тест: один вопрос на экран ───────────────────────────────────────

/** Ответ на вопрос дан (по типу вопроса) — для «Далее», точек и «Отправить». */
export function isAnswered(q: QuizClientQuestion, a: QuizAnswerInput | undefined): boolean {
  if (!a) return false;
  switch (q.kind) {
    case 'single':
    case 'multi':
    case 'true_false':
      return (a.selectedOptionIds?.length ?? 0) > 0;
    case 'matching': {
      const n = Array.isArray(q.payload?.left) ? (q.payload!.left as unknown[]).length : 0;
      return Array.isArray(a.value) && a.value.length === n && (a.value as unknown[]).every((v) => Number.isInteger(v));
    }
    case 'ordering':
      return Array.isArray(a.value) && a.value.length > 0;
    case 'hotspot':
      return !!a.value && typeof a.value === 'object';
    case 'photo_upload': {
      const v = a.value && typeof a.value === 'object' ? (a.value as { attachmentId?: string; note?: string }) : null;
      return !!v && (!!v.attachmentId || !!v.note?.trim());
    }
    default:
      return typeof a.value === 'string' && a.value.trim().length > 0;
  }
}

export type DotState = 'current' | 'answered' | 'empty';

/** Точки-навигатор: текущий вопрос, отвеченные, пустые. */
export function quizDots(
  questions: QuizClientQuestion[],
  answers: Record<string, QuizAnswerInput>,
  current: number,
): DotState[] {
  return questions.map((q, i) => (i === current ? 'current' : isAnswered(q, answers[q.id]) ? 'answered' : 'empty'));
}

/** Первый неотвеченный вопрос (для «Отправить» с пропусками); -1 — все отвечены. */
export function firstUnanswered(questions: QuizClientQuestion[], answers: Record<string, QuizAnswerInput>): number {
  return questions.findIndex((q) => !isAnswered(q, answers[q.id]));
}

/** «8 вопросов», «1 вопрос». */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

/** Подпись типа вопроса на экране вопроса. */
export const QUESTION_KIND_LABEL: Record<string, string> = {
  single: 'Один ответ',
  multi: 'Несколько ответов',
  true_false: 'Верно / неверно',
  matching: 'Сопоставление',
  ordering: 'Порядок',
  open: 'Открытый ответ',
  hotspot: 'Точка на картинке',
  photo_upload: 'Фото-ответ',
};

// ─── Траектория: полоска дней ─────────────────────────────────────────

const DAY = 86_400_000;

export interface PathDays {
  /** Сегодня — день N (1…total; после срока — total). */
  day: number;
  total: number;
  cells: Array<'past' | 'today' | 'future'>;
  /** Срок прошёл, траектория не завершена. */
  overdue: boolean;
}

/**
 * Дни траектории: от назначения до дедлайна (иначе — до самого позднего
 * срока шага, иначе 14 дней). Полоска — не длиннее 60 клеток.
 */
export function pathDays(assignedAt: string, dueAt: string | null, stepDues: Array<string | null>, now = Date.now()): PathDays {
  const start = new Date(assignedAt).getTime();
  const ends = [dueAt, ...stepDues].filter((x): x is string => !!x).map((x) => new Date(x).getTime()).filter((t) => Number.isFinite(t));
  const end = dueAt ? new Date(dueAt).getTime() : ends.length ? Math.max(...ends) : start + 13 * DAY;
  const total = Math.max(1, Math.min(60, Math.round((end - start) / DAY) + 1));
  const raw = Math.floor((now - start) / DAY) + 1;
  const day = Math.max(1, Math.min(total, raw));
  const cells = Array.from({ length: total }, (_, i) => (i + 1 < day ? 'past' : i + 1 === day ? 'today' : 'future') as 'past' | 'today' | 'future');
  return { day, total, cells, overdue: raw > total };
}

// ─── Наставник: «Ждут подтверждения» ──────────────────────────────────

export interface PendingConfirmation {
  ownerKey: string;
  name: string;
  skillId: string;
  skillName: string;
  /** test — тест сдан, ждёт чек-листа; expiring — подтверждение истекает ≤ 30 дней; expired — истекло. */
  reason: 'test' | 'expiring' | 'expired';
  /** Дней до истечения (для expiring). */
  days: number | null;
}

/**
 * Кого наставнику подтвердить: уровень 2 (тест сдан) по навыку с чек-листом,
 * истёкшие и истекающие в 30 дней подтверждения. Сначала истёкшие, потом
 * истекающие (раньше — выше), потом сдавшие тест.
 */
export function pendingConfirmations(
  team: {
    skills: Array<{ id: string; name: string; hasChecklist: boolean }>;
    members: Array<{ ownerKey: string; name: string; skills: Array<{ skillId: string; level: number; expiresAt: string | null; expired: boolean }> }>;
  },
  now = Date.now(),
): PendingConfirmation[] {
  const byId = new Map(team.skills.map((s) => [s.id, s]));
  const out: PendingConfirmation[] = [];
  for (const m of team.members) {
    for (const u of m.skills) {
      const s = byId.get(u.skillId);
      if (!s?.hasChecklist) continue;
      const exp = u.expiresAt ? new Date(u.expiresAt).getTime() : null;
      const days = exp != null ? Math.ceil((exp - now) / DAY) : null;
      if (u.expired) out.push({ ownerKey: m.ownerKey, name: m.name, skillId: s.id, skillName: s.name, reason: 'expired', days });
      else if (u.level >= 3 && days != null && days <= 30) out.push({ ownerKey: m.ownerKey, name: m.name, skillId: s.id, skillName: s.name, reason: 'expiring', days });
      else if (u.level === 2) out.push({ ownerKey: m.ownerKey, name: m.name, skillId: s.id, skillName: s.name, reason: 'test', days: null });
    }
  }
  const rank = { expired: 0, expiring: 1, test: 2 } as const;
  return out.sort((a, b) => rank[a.reason] - rank[b.reason] || (a.days ?? 0) - (b.days ?? 0) || a.name.localeCompare(b.name, 'ru'));
}

/** Инициалы для аватара: «Соколова Ирина» → «СИ». */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';
}
